import React, { useMemo, useState } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AddressDisplay } from "@/components/AddressDisplay";
import { formatUSD, timeAgo } from "@/lib/format";
import { Search, RefreshCw } from "lucide-react";

const EVENT_TYPES = [
  "All", "Mint", "Burn", "Transfer", "Paused", "Unpaused",
  "Blacklisted", "UnBlacklisted", "MinterConfigured", "MinterRemoved",
  "RoleGranted", "RoleRevoked", "MaxSupplyUpdated", "TokensRecovered", "Upgraded",
];

const TAG = {
  Mint: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Burn: "bg-red-50 text-red-700 border-red-200",
  Paused: "bg-amber-50 text-amber-700 border-amber-200",
  Unpaused: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Blacklisted: "bg-slate-100 text-slate-900 border-slate-300",
  UnBlacklisted: "bg-slate-50 text-slate-700 border-slate-200",
  RoleGranted: "bg-slate-50 text-slate-700 border-slate-200",
  RoleRevoked: "bg-slate-50 text-slate-700 border-slate-200",
  MinterConfigured: "bg-emerald-50 text-emerald-700 border-emerald-200",
  MinterRemoved: "bg-red-50 text-red-700 border-red-200",
  Transfer: "bg-slate-50 text-slate-700 border-slate-200",
  Upgraded: "bg-slate-50 text-slate-700 border-slate-200",
  TokensRecovered: "bg-amber-50 text-amber-700 border-amber-200",
  MaxSupplyUpdated: "bg-slate-50 text-slate-700 border-slate-200",
};

export default function AuditLog() {
  const { audit, token, refreshAudit } = useApp();
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    return audit.filter((e) => {
      if (filter !== "All" && e.event_type !== filter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          e.actor?.toLowerCase().includes(s) ||
          e.subject?.toLowerCase().includes(s) ||
          e.tx_hash?.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [audit, filter, search]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Compliance Audit Log</div>
          <h1 className="text-3xl font-semibold tracking-tight">Immutable activity feed</h1>
        </div>
        <Button variant="outline" size="sm" className="rounded-md border-slate-200" onClick={refreshAudit}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />Refresh
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <Input className="pl-9 font-mono" placeholder="Search by address or tx hash" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        <div className="w-48">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Badge variant="outline" className="font-mono text-[10px]">{rows.length} events</Badge>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader className="sticky top-0 bg-slate-50">
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Event</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Actor</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Subject</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-slate-500">Amount</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Tx</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">When</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((e, i) => (
              <TableRow key={e.id || i} data-testid={`event-row-${i}`}>
                <TableCell>
                  <span className={`inline-flex items-center text-[10px] font-mono uppercase tracking-widest px-2 py-1 border rounded ${TAG[e.event_type] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                    {e.event_type}
                  </span>
                </TableCell>
                <TableCell><AddressDisplay value={e.actor} /></TableCell>
                <TableCell><AddressDisplay value={e.subject} /></TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {e.amount ? `${formatUSD(BigInt(e.amount), token.decimals)} ${token.symbol}` : "—"}
                </TableCell>
                <TableCell><AddressDisplay value={e.tx_hash} lead={6} tail={6} /></TableCell>
                <TableCell className="text-xs font-mono text-slate-500">{timeAgo(e.timestamp)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-sm text-slate-500 px-5 py-8 text-center">No events match the filter.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
